/**
 * Branded full-screen shell shared by every public/auth surface:
 *   - app/(auth)/signup.tsx
 *   - app/[slug].tsx       (public deal landing)
 *   - app/index.tsx        (web cold-start landing)
 *
 * Athlete background image + Deep Green overlay + safe area + keyboard
 * avoidance + scroll. Children render inside the scroll container.
 *
 * The `centered` prop controls whether children sit in the vertical
 * center (signup card alone) or flow top-down (deal landing with hero
 * + sections). Pick once per route — no internal toggle.
 */
import { Stack } from 'expo-router';
import {
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { colors, spacing } from '@/constants/theme';

export interface BrandedAuthShellProps {
  children: React.ReactNode;
  /**
   * When true (default), children sit centered vertically — fits the
   * "signup card alone" layout used by `(auth)/signup` and the web
   * cold-start landing. Pass false for top-down flow (deal landing
   * with hero + below-the-fold sections).
   */
  centered?: boolean;
  /** Hide the athlete background and overlay (rare). */
  plain?: boolean;
}

export function BrandedAuthShell({
  children,
  centered = true,
  plain = false,
}: BrandedAuthShellProps) {
  return (
    <View style={styles.root}>
      <Stack.Screen options={{ headerShown: false }} />
      {!plain && (
        <>
          <Image
            source={require('@/assets/images/athlete-bg.jpg')}
            style={styles.bg}
            resizeMode="cover"
          />
          <View style={styles.overlay} />
        </>
      )}

      <SafeAreaView style={styles.safe} edges={['top', 'bottom']}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={styles.flex}
        >
          <ScrollView
            contentContainerStyle={[
              styles.scroll,
              centered ? styles.scrollCentered : styles.scrollFlow,
            ] as ViewStyle[]}
            keyboardShouldPersistTaps="handled"
          >
            {children}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.deepGreen },
  bg: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.deepGreenOverlay,
  },
  safe: { flex: 1 },
  flex: { flex: 1 },
  scroll: { flexGrow: 1 },
  scrollCentered: {
    justifyContent: 'center',
    padding: spacing.lg,
    alignItems: 'center',
  },
  scrollFlow: { alignItems: 'center' },
});
